import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import { CSRF_HEADER, getCsrfToken } from '../services/csrf';
import { taggedFetch } from '../services/taggedFetch';
import './FeatureFlagsPage.css';

interface RoleAssignment {
    role_id: string;
    role_name: string;
    enabled: boolean;
}

interface FeatureFlagDetail {
    id: string;
    flag_id: string;
    name: string;
    description: string | null;
    layer: string;
    created_at: string;
    roles: RoleAssignment[];
}

const LAYER_LABELS: Record<string, string> = {
    mock_ui: 'Mock UI',
    twin: 'Digital Twin',
    admin: 'Administration',
};

const FeatureFlagsPage: React.FC = () => {
    const { t } = useTranslation();
    const { refreshFlags } = useFeatureFlags();
    const [flags, setFlags] = useState<FeatureFlagDetail[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState<string | null>(null);

    const fetchFlags = useCallback(async () => {
        try {
            const response = await taggedFetch('/api/v1/admin/features', {
                credentials: 'include',
            });
            if (response.status === 403) {
                setError('Access denied. Admin privileges required.');
                setLoading(false);
                return;
            }
            if (!response.ok) {
                throw new Error('Failed to fetch feature flags');
            }
            const data = await response.json();
            setFlags(data.flags);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFlags();
    }, [fetchFlags]);

    const handleToggle = async (flagId: string, roleId: string, enabled: boolean) => {
        const key = `${flagId}:${roleId}`;
        setSaving(key);
        try {
            // #1217 D1: this admin toggle uses native fetch (not the axios api
            // client), and /admin/ is NOT a BFF CSRF-exempt path, so echo the
            // double-submit token explicitly or the PUT 403s once enforcement
            // is on. #1245: routed through `taggedFetch` so this user-initiated
            // toggle still carries `X-User-Interaction`; the wrapper preserves
            // the CSRF header set below.
            const csrfToken = getCsrfToken();
            const response = await taggedFetch(`/api/v1/admin/features/${flagId}/roles/${roleId}`, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    ...(csrfToken ? { [CSRF_HEADER]: csrfToken } : {}),
                },
                body: JSON.stringify({ enabled }),
            });
            if (!response.ok) {
                throw new Error('Failed to update flag');
            }
            // Update local state
            setFlags((prev) =>
                prev.map((flag) =>
                    flag.flag_id === flagId
                        ? {
                            ...flag,
                            roles: flag.roles.map((r) =>
                                r.role_id === roleId ? { ...r, enabled } : r,
                            ),
                        }
                        : flag,
                ),
            );
            // Refresh the current user's flags in context
            await refreshFlags();
        } catch (err) {
            console.error('Toggle failed:', err);
        } finally {
            setSaving(null);
        }
    };

    // Group flags by layer
    const flagsByLayer = flags.reduce<Record<string, FeatureFlagDetail[]>>((acc, flag) => {
        (acc[flag.layer] ??= []).push(flag);
        return acc;
    }, {});

    // Get unique role names from first flag (all flags have the same roles)
    const roleNames = flags.length > 0 ? flags[0].roles.map((r) => r.role_name) : [];

    if (loading) {
        return <div className="ff-loading">{t('common.loading', 'Loading...')}</div>;
    }

    if (error) {
        return (
            <div className="ff-error">
                <p>{error}</p>
            </div>
        );
    }

    return (
        <div className="ff-page">
            <div className="ff-header">
                <h1>{t('admin.featureFlags', 'Feature Flags')}</h1>
                <p className="ff-subtitle">
                    {t('admin.featureFlagsSubtitle', 'Control which features are visible per role')}
                </p>
            </div>

            {Object.entries(flagsByLayer).map(([layer, layerFlags]) => (
                <div key={layer} className="ff-layer">
                    <h2 className="ff-layer__title">
                        {LAYER_LABELS[layer] ?? layer}
                    </h2>
                    <div className="ff-table-container">
                        <table className="ff-table">
                            <thead>
                                <tr>
                                    <th className="ff-table__flag-col">Flag</th>
                                    {roleNames.map((role) => (
                                        <th key={role} className="ff-table__role-col">
                                            {role}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {layerFlags.map((flag) => (
                                    <tr key={flag.flag_id}>
                                        <td className="ff-table__flag-cell">
                                            <div className="ff-flag-info">
                                                <code className="ff-flag-id">{flag.flag_id}</code>
                                                <span className="ff-flag-name">{flag.name}</span>
                                                {flag.description && (
                                                    <span className="ff-flag-desc">{flag.description}</span>
                                                )}
                                            </div>
                                        </td>
                                        {flag.roles.map((role) => {
                                            const key = `${flag.flag_id}:${role.role_id}`;
                                            return (
                                                <td key={role.role_id} className="ff-table__toggle-cell">
                                                    <label className="ff-toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={role.enabled}
                                                            disabled={saving === key}
                                                            onChange={(e) =>
                                                                handleToggle(flag.flag_id, role.role_id, e.target.checked)
                                                            }
                                                        />
                                                        <span className="ff-toggle__slider" />
                                                    </label>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default FeatureFlagsPage;
